import { _isNil, _isNotEmpty } from '@/littledash';
import type {
  SendCapabilityReport,
  SendCrossDomainTier,
  SendExactPass,
  SendResultReport,
  SendRunScope,
} from '@/model/Agent.model';
import { passedInPartOf } from './SendRuleOutcomes';
import { countOf } from './SendText';

/**
 * The parts of a SEND result's header that results stored before they were published lack, read as
 * possibly absent whatever the generated type says, since adding them did not change the schema
 * version: the cross-domain checks' own counts, what the build can check, and Java's exact-pass verdicts.
 */

/** A result's header as stored: one stored before these were published has none of them. */
interface StoredReport {
  cross_domain?: SendCrossDomainTier;
  exact_pass?: SendExactPass;
  capability?: SendCapabilityReport;
}

/** The cross-domain checks' own counts, as the server published them, or nothing for an older result. */
export const crossDomainTierOf = (report: SendResultReport): SendCrossDomainTier | undefined => {
  const stored: StoredReport = report;
  return stored.cross_domain;
};

/** What this build can check, per population, or nothing for an older result. */
export const capabilityOf = (report: SendResultReport): SendCapabilityReport | undefined => {
  const stored: StoredReport = report;
  return stored.capability;
};

/**
 * Java's exact-pass verdicts, as published, but only for a study in scope: an out-of-scope study was not
 * checked, and one of unknown scope was checked against an assumed version, so neither passed
 * whatever the verdicts say. An older result, with no verdicts, never passed.
 */
export const exactPassOf = (report: SendResultReport, scope: SendRunScope | undefined): SendExactPass | undefined => {
  const stored: StoredReport = report;
  if (_isNil(stored.exact_pass)) {
    return undefined;
  }
  return scope?.state === 'in_scope'
    ? stored.exact_pass
    : {
        run: false,
        register: false,
        encodings_outside_register: false,
        cross_domain: false,
        by_dataset: stored.exact_pass.by_dataset.map(({ key }) => ({ key, exact_pass: false })),
      };
};

/** The parts of a run that must each pass exactly, in the words the run header names them. */
const EXACT_PASS_PARTS: ReadonlyArray<{
  key: 'register' | 'encodings_outside_register' | 'cross_domain';
  text: string;
}> = [
  { key: 'register', text: 'the published rules' },
  { key: 'encodings_outside_register', text: 'the encodings outside the register' },
  { key: 'cross_domain', text: 'the cross-domain checks' },
];

/**
 * Whether the result left anything unsettled: a published rule or encoding not evaluated or passed only in
 * part, or a check, in a file or across them, that could not decide everything it read.
 */
const leftAGap = (report: SendResultReport): boolean =>
  report.tally.not_evaluated > 0 ||
  passedInPartOf(report.tally) > 0 ||
  report.encoding_tally.not_evaluated > 0 ||
  passedInPartOf(report.encoding_tally) > 0 ||
  report.summary.undecided_checks > 0 ||
  report.cross_domain_undecided_checks > 0;

/**
 * Whether the run passed exactly, as the server decided, and which parts stopped it. Never a
 * pass for a study out of scope or of unknown scope, never a verdict the result doesn't record, and never,
 * whatever the verdict says, a pass over a rule left unchecked or a check left undecided.
 */
export const runVerdictText = (
  exactPass: SendExactPass | undefined,
  scope: SendRunScope | undefined,
  report: SendResultReport
): string => {
  if (_isNil(exactPass)) {
    return "This result doesn't record whether the run passed every check this validator runs in full.";
  }
  if (scope?.state === 'out_of_scope') {
    return 'Not validated: the study is out of scope.';
  }
  if (scope?.state === 'scope_unknown') {
    return "Not passed: the study's own SENDIG version isn't known.";
  }
  if (exactPass.run && !leftAGap(report)) {
    return 'The run passed every check this validator runs, each in full.';
  }
  const stopped = EXACT_PASS_PARTS.filter(({ key }) => !exactPass[key]).map(({ text }) => text);
  const datasets = exactPass.by_dataset.filter((dataset) => !dataset.exact_pass).length;
  if (_isNotEmpty(stopped)) {
    const parts =
      stopped.length > 1 ? `${stopped.slice(0, -1).join(', ')} and ${stopped[stopped.length - 1]}` : stopped[0];
    return `Not passed exactly: ${parts} didn't all pass exactly.`;
  }
  return datasets > 0
    ? `Not passed exactly: ${countOf(datasets, 'dataset', 'datasets')} did not pass exactly.`
    : 'Not passed exactly: the result found a problem or left a check undecided.';
};
